import React, { useMemo, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CloudUpload,
  CreateNewFolder,
  Delete,
  Download,
  Edit,
  FindInPage,
  Image,
  InsertDriveFile,
  MoreVert,
  PictureAsPdf,
  PictureInPicture,
  Search,
  UploadFile,
} from '@mui/icons-material';

import { IDocument, normalizeDocument } from '../../Interfaces/IDocument';
import { IDocumentDirectory } from '../../Interfaces/IDocumentDirectory';
import { IDocumentDirectoryHasDocument } from '../../Interfaces/IDocumentDirectoryHasDocument';
import { IDocumentDownloaded } from '../../Interfaces/IDocumentDownloaded';
import { ILegalClient } from '../../Interfaces/ILegalClient';
import { IMIMEType } from '../../Interfaces/IMIMEType';
import { State } from '../../redux/mainReducer';
import { fileToBase64 } from '../../Document/DocumentOverview';
import { DocumentPreviewDialog } from '../../Document/DocumentAssistant/DocumentPreviewDialog';
import { DocumentDownloadedDialog } from '../../Document/DocumentAssistant/DocumentDownloadedDialog';
import { getFetch } from '../../hooks/useFetch';
import { useSidepanelDocumentContext } from '../LegalTransactionEdit';
import { useDocumentTransferActions } from '../DocumentTransfer/DocumentTransferActions';
import {
  downloadDocument,
  getFileExtension,
  getMimeTypeString,
} from './documentDownload';
import {
  countDocumentsInDirectoryTree,
  getChildDirectories,
  getDescendantDirectoryIds,
  getDirectoryPath,
  getDocumentsOfDirectory,
  getNextTempId,
} from './documentDirectoryUtils';
import {
  getDirectoryType,
  getDirectoryTypeInfo,
  isSystemDirectory,
} from './documentDirectoryTypes';
import {
  ISystemDirectoryDocument,
  ISystemDirectorySource,
  getSystemDirectoryDocuments,
} from './systemDirectoryContent';

interface IProps {
  documentDirectoryArray: IDocumentDirectory[];
  setDocumentDirectoryArray: (
    documentDirectoryArray: IDocumentDirectory[]
  ) => void;
  documentArray: IDocumentDirectoryHasDocument[];
  setDocumentArray: (documentArray: IDocumentDirectoryHasDocument[]) => void;
  selectedDirectoryId: number | null;
  setSelectedDirectoryId: (idDocumentDirectory: number | null) => void;
  systemSource: ISystemDirectorySource;
  onRequestNewDirectory: (parent_idDocumentDirectory: number | null) => void;
  onDeleteDirectory: (
    idDocumentDirectory: number,
    moveDocumentsTo?: number
  ) => void;
  dropTargetId: number | null | undefined;
  onDragOverDirectory: (idDocumentDirectory: number | null) => void;
  onDragLeaveDirectory: () => void;
  onDropOnDirectory: (idDocumentDirectory: number | null) => void;
  onDragStartDirectory: (idDocumentDirectory: number) => void;
  onDragStartDocument: (idDocument: number) => void;
  /** Download-Protokoll des Vorgangs — blendet die Spalte „Heruntergeladen" ein. */
  downloadedArray?: IDocumentDownloaded[];
  assignedLegalClients?: ILegalClient[];
  isReadOnly: boolean;
}

type IContentEntry =
  | { kind: 'directory'; directory: IDocumentDirectory }
  | { kind: 'document'; documentObject: IDocumentDirectoryHasDocument }
  | { kind: 'mirror'; documentObject: ISystemDirectoryDocument };

const formatDate = (value: string | null | undefined): string => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const parsed = new Date(value);

  return Number.isNaN(parsed.getTime())
    ? ''
    : parsed.toLocaleDateString('de-DE');
};

const getFileIcon = (
  fileName: string,
  idMIMEType: number,
  mimeTypeArray: IMIMEType[]
) => {
  const extension = getFileExtension(fileName, idMIMEType, mimeTypeArray);

  if (extension === 'pdf') {
    return <PictureAsPdf fontSize='small' color='error' />;
  }

  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'tif', 'tiff'].includes(extension)) {
    return <Image fontSize='small' color='primary' />;
  }

  return <InsertDriveFile fontSize='small' color='action' />;
};

/**
 * Rechte Haelfte des Dateibrowsers: Inhalt des aktuell ausgewaehlten Ordners.
 * Standardordner sind vollstaendig bearbeitbar, Systemordner spiegeln ihren
 * Inhalt und sind daher schreibgeschuetzt.
 */
export const DocumentDirectoryContent: React.FC<IProps> = props => {
  const mimeTypeArray = useSelector(
    (state: State) => state.mimeTypeArray.storeAcademicTitle
  );
  const { setSelectedSidepanelDocument } = useSidepanelDocumentContext();

  const [isDownloading, setIsDownloading] = useState(false);
  const [uploadWarning, setUploadWarning] = useState('');
  const [menuEntry, setMenuEntry] = useState<IContentEntry | null>(null);
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [renameEntry, setRenameEntry] = useState<IContentEntry | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteDirectory, setDeleteDirectory] =
    useState<IDocumentDirectory | null>(null);
  const [previewDocument, setPreviewDocument] = useState<IDocument | null>(
    null
  );
  const [deleteDocumentMode, setDeleteDocumentMode] = useState<
    'delete' | 'move'
  >('move');
  const [deleteMoveTargetId, setDeleteMoveTargetId] = useState<number | ''>('');
  const [transferDocument, setTransferDocument] = useState<IDocument | null>(
    null
  );
  const [downloadedDocument, setDownloadedDocument] =
    useState<IDocument | null>(null);

  const uploadInputRef = useRef<HTMLInputElement | null>(null);

  const transferActions = useDocumentTransferActions({
    documentObject: transferDocument,
    location:
      props.selectedDirectoryId === null
        ? undefined
        : {
            kind: 'directory',
            idDocumentDirectory: props.selectedDirectoryId,
          },
    onOpenDialog: () => handleCloseMenu(),
  });

  const currentDirectory = useMemo(
    () =>
      props.documentDirectoryArray.find(
        documentDirectory =>
          documentDirectory.idDocumentDirectory === props.selectedDirectoryId
      ),
    [props.documentDirectoryArray, props.selectedDirectoryId]
  );

  const directoryType = getDirectoryType(currentDirectory);
  const isSystem =
    currentDirectory !== undefined && isSystemDirectory(currentDirectory);
  const isRoot = props.selectedDirectoryId === null;
  const canModify = !props.isReadOnly && !isSystem;

  const path = useMemo(
    () =>
      getDirectoryPath(props.documentDirectoryArray, props.selectedDirectoryId),
    [props.documentDirectoryArray, props.selectedDirectoryId]
  );

  const entryArray: IContentEntry[] = useMemo(() => {
    const childDirectoryArray = getChildDirectories(
      props.documentDirectoryArray,
      props.selectedDirectoryId
    ).map(
      (directory): IContentEntry => ({
        kind: 'directory',
        directory: directory,
      })
    );

    if (isRoot) {
      return childDirectoryArray;
    }

    if (isSystem) {
      return [
        ...childDirectoryArray,
        ...getSystemDirectoryDocuments(directoryType, props.systemSource).map(
          (documentObject): IContentEntry => ({
            kind: 'mirror',
            documentObject: documentObject,
          })
        ),
      ];
    }

    return [
      ...childDirectoryArray,
      ...getDocumentsOfDirectory(
        props.documentArray,
        props.selectedDirectoryId as number
      ).map(
        (documentObject): IContentEntry => ({
          kind: 'document',
          documentObject: documentObject,
        })
      ),
    ];
  }, [
    props.documentDirectoryArray,
    props.documentArray,
    props.selectedDirectoryId,
    props.systemSource,
    directoryType,
    isRoot,
    isSystem,
  ]);

  /** Dateien im zu loeschenden Ordner einschliesslich Unterordner. */
  const deleteDocumentCount =
    deleteDirectory === null
      ? 0
      : countDocumentsInDirectoryTree(
          deleteDirectory.idDocumentDirectory,
          props.documentDirectoryArray,
          props.documentArray
        );

  /** Moegliche Zielordner: alles ausser dem Ordner selbst, seinen Unterordnern
   *  und den Systemordnern. */
  const deleteMoveTargetArray = useMemo(() => {
    if (deleteDirectory === null) {
      return [];
    }

    const excludedIdArray = [
      deleteDirectory.idDocumentDirectory,
      ...getDescendantDirectoryIds(
        props.documentDirectoryArray,
        deleteDirectory.idDocumentDirectory
      ),
    ];
    const result: {
      idDocumentDirectory: number;
      Title: string;
      depth: number;
    }[] = [];

    const collect = (
      parent_idDocumentDirectory: number | null,
      depth: number
    ) => {
      getChildDirectories(
        props.documentDirectoryArray,
        parent_idDocumentDirectory
      ).forEach(documentDirectory => {
        if (
          !isSystemDirectory(documentDirectory) &&
          !excludedIdArray.includes(documentDirectory.idDocumentDirectory)
        ) {
          result.push({
            idDocumentDirectory: documentDirectory.idDocumentDirectory,
            Title: documentDirectory.Title,
            depth: depth,
          });
        }

        collect(documentDirectory.idDocumentDirectory, depth + 1);
      });
    };

    collect(null, 0);

    return result;
  }, [deleteDirectory, props.documentDirectoryArray]);

  /** Loeschdialog oeffnen und die Vorbelegung passend setzen. */
  const handleStartDeleteDirectory = (
    documentDirectory: IDocumentDirectory
  ) => {
    const firstTarget = deleteMoveTargetArrayFor(documentDirectory);

    setDeleteDirectory(documentDirectory);
    setDeleteDocumentMode(firstTarget === null ? 'delete' : 'move');
    setDeleteMoveTargetId(firstTarget ?? '');
  };

  /** Erster moeglicher Zielordner (fuer die Vorbelegung im Dialog). */
  const deleteMoveTargetArrayFor = (
    documentDirectory: IDocumentDirectory
  ): number | null => {
    const excludedIdArray = [
      documentDirectory.idDocumentDirectory,
      ...getDescendantDirectoryIds(
        props.documentDirectoryArray,
        documentDirectory.idDocumentDirectory
      ),
    ];
    const candidate = props.documentDirectoryArray.find(
      entry =>
        !isSystemDirectory(entry) &&
        !excludedIdArray.includes(entry.idDocumentDirectory)
    );

    return candidate === undefined ? null : candidate.idDocumentDirectory;
  };

  const handleUpload = async (fileArray: File[]) => {
    if (!canModify || isRoot || fileArray.length === 0) {
      return;
    }

    const rejectedArray: string[] = [];
    const uploadArray: IDocumentDirectoryHasDocument[] = [];
    let nextId = getNextTempId(
      props.documentArray.map(documentObject => documentObject.idDocument)
    );

    for (const file of fileArray) {
      const mimeType = mimeTypeArray.find(
        currentMimeType => currentMimeType.MIMEType === file.type
      );

      if (mimeType === undefined) {
        rejectedArray.push(file.name);
        continue;
      }

      uploadArray.push({
        idDocument: nextId,
        FileName: file.name,
        idMIMEType: mimeType.idMIMEType,
        Data: await fileToBase64(file),
        Created_at: new Date().toISOString(),
        idDocumentDirectory: props.selectedDirectoryId as number,
        idLegalTransaction: -1,
        perEmail: false,
        isFromTemplate: false,
      } as IDocumentDirectoryHasDocument);

      nextId = nextId - 1;
    }

    if (uploadArray.length > 0) {
      props.setDocumentArray([...props.documentArray, ...uploadArray]);
    }

    setUploadWarning(
      rejectedArray.length === 0
        ? ''
        : `Nicht unterstützte Dateiart: ${rejectedArray.join(', ')}`
    );
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFileArray: File[]) => {
      handleUpload(acceptedFileArray);
    },
    noClick: true,
    noKeyboard: true,
    disabled: !canModify || isRoot,
  });

  /** Wie oft wurde die Datei bereits vom Mandanten heruntergeladen? */
  const getDownloadCount = (idDocument: number): number =>
    (props.downloadedArray ?? []).filter(
      entry => entry.idDocument === idDocument
    ).length;

  /** Dokument im schwebenden Seitenpanel (Bild-in-Bild) anzeigen. */
  const handleOpenSidepanel = (
    documentObject: Pick<
      IDocument,
      'idDocument' | 'FileName' | 'idMIMEType'
    > & { Data?: string }
  ) => {
    const panelDocument = {
      ...documentObject,
      Created_at: '',
      idDocumentDirectory: null,
      idLegalTransaction: null,
      perEmail: false,
      isFromTemplate: false,
    } as IDocument;

    setSelectedSidepanelDocument(panelDocument);

    if (panelDocument.Data === undefined) {
      getFetch('/document/', panelDocument.idDocument, (loaded: IDocument) => {
        if (loaded !== null && loaded !== undefined) {
          setSelectedSidepanelDocument(normalizeDocument(loaded));
        }
      });
    }
  };

  const handleOpenMenu = (event: React.MouseEvent, entry: IContentEntry) => {
    event.preventDefault();
    event.stopPropagation();
    setMenuEntry(entry);
    setTransferDocument(
      entry.kind === 'document' ? (entry.documentObject as IDocument) : null
    );
    setMenuPosition({ top: event.clientY, left: event.clientX });
  };

  const handleCloseMenu = () => {
    setMenuEntry(null);
    setMenuPosition(null);
  };

  const handleDownload = (
    documentObject: Pick<
      IDocument,
      'idDocument' | 'FileName' | 'idMIMEType'
    > & {
      Data?: string;
    }
  ) => {
    downloadDocument(documentObject, mimeTypeArray, setIsDownloading);
  };

  /** Vorschau oeffnen; fehlende Dateiinhalte werden dafuer nachgeladen. */
  const handleOpenPreview = (
    documentObject: Pick<
      IDocument,
      'idDocument' | 'FileName' | 'idMIMEType'
    > & {
      Data?: string;
    }
  ) => {
    const previewObject = {
      ...documentObject,
      Created_at: '',
      idDocumentDirectory: null,
      idLegalTransaction: null,
      perEmail: false,
      isFromTemplate: false,
    } as IDocument;

    setPreviewDocument(previewObject);

    if (previewObject.Data === undefined) {
      getFetch('/document/', previewObject.idDocument, (loaded: IDocument) => {
        if (loaded !== null && loaded !== undefined) {
          setPreviewDocument(loaded);
        }
      });
    }
  };

  const handleStartRename = (entry: IContentEntry) => {
    setRenameEntry(entry);
    setRenameValue(
      entry.kind === 'directory'
        ? entry.directory.Title
        : entry.documentObject.FileName
    );
  };

  const handleConfirmRename = () => {
    if (renameEntry === null || renameValue.trim() === '') {
      return;
    }

    if (renameEntry.kind === 'directory') {
      props.setDocumentDirectoryArray(
        props.documentDirectoryArray.map(documentDirectory =>
          documentDirectory.idDocumentDirectory ===
          renameEntry.directory.idDocumentDirectory
            ? { ...documentDirectory, Title: renameValue.trim() }
            : documentDirectory
        )
      );
    } else if (renameEntry.kind === 'document') {
      props.setDocumentArray(
        props.documentArray.map(documentObject =>
          documentObject.idDocument === renameEntry.documentObject.idDocument
            ? { ...documentObject, FileName: renameValue.trim() }
            : documentObject
        )
      );
    }

    setRenameEntry(null);
  };

  const handleDeleteDocument = (idDocument: number) => {
    props.setDocumentArray(
      props.documentArray.filter(
        documentObject => documentObject.idDocument !== idDocument
      )
    );
  };

  const renderBreadcrumbs = () => (
    <Breadcrumbs sx={{ mb: 1 }}>
      <Link
        component='button'
        type='button'
        underline='hover'
        color={isRoot ? 'text.primary' : 'inherit'}
        onClick={() => props.setSelectedDirectoryId(null)}
        onDragOver={(event: React.DragEvent) => {
          event.preventDefault();
          props.onDragOverDirectory(null);
        }}
        onDrop={(event: React.DragEvent) => {
          event.preventDefault();
          props.onDropOnDirectory(null);
        }}
      >
        Interne Datenablage
      </Link>
      {path.map((documentDirectory, index) => (
        <Link
          key={`breadcrumb-${documentDirectory.idDocumentDirectory}`}
          component='button'
          type='button'
          underline='hover'
          color={index === path.length - 1 ? 'text.primary' : 'inherit'}
          onClick={() =>
            props.setSelectedDirectoryId(documentDirectory.idDocumentDirectory)
          }
          onDragOver={(event: React.DragEvent) => {
            event.preventDefault();
            props.onDragOverDirectory(documentDirectory.idDocumentDirectory);
          }}
          onDrop={(event: React.DragEvent) => {
            event.preventDefault();
            props.onDropOnDirectory(documentDirectory.idDocumentDirectory);
          }}
        >
          {documentDirectory.Title}
        </Link>
      ))}
    </Breadcrumbs>
  );

  const renderRow = (entry: IContentEntry, index: number) => {
    if (entry.kind === 'directory') {
      const rowDirectoryType = getDirectoryType(entry.directory);
      const info = getDirectoryTypeInfo(rowDirectoryType);
      const Icon = info.icon;

      return (
        <TableRow
          key={`entry-directory-${entry.directory.idDocumentDirectory}`}
          hover
          sx={{
            cursor: 'pointer',
            backgroundColor:
              props.dropTargetId === entry.directory.idDocumentDirectory
                ? 'action.selected'
                : undefined,
          }}
          draggable={!props.isReadOnly}
          onDragStart={() =>
            props.onDragStartDirectory(entry.directory.idDocumentDirectory)
          }
          onDragOver={(event: React.DragEvent) => {
            event.preventDefault();
            props.onDragOverDirectory(entry.directory.idDocumentDirectory);
          }}
          onDragLeave={() => props.onDragLeaveDirectory()}
          onDrop={(event: React.DragEvent) => {
            event.preventDefault();
            event.stopPropagation();
            props.onDropOnDirectory(entry.directory.idDocumentDirectory);
          }}
          onClick={() =>
            props.setSelectedDirectoryId(entry.directory.idDocumentDirectory)
          }
          onContextMenu={(event: React.MouseEvent) =>
            handleOpenMenu(event, entry)
          }
        >
          <TableCell>
            <Stack direction='row' spacing={1} alignItems='center'>
              <Icon
                fontSize='small'
                color={rowDirectoryType === 'standard' ? 'action' : 'primary'}
              />
              <Typography variant='body2'>{entry.directory.Title}</Typography>
            </Stack>
          </TableCell>
          <TableCell>
            {rowDirectoryType === 'standard' ? (
              'Ordner'
            ) : (
              <Chip
                size='small'
                color='primary'
                variant='outlined'
                label={info.label}
              />
            )}
          </TableCell>
          <TableCell>{formatDate(entry.directory.Created_at)}</TableCell>
          {props.downloadedArray !== undefined && <TableCell />}
          <TableCell
            align='right'
            onClick={(event: React.MouseEvent) => event.stopPropagation()}
          >
            <IconButton
              size='small'
              onClick={(event: React.MouseEvent) =>
                handleOpenMenu(event, entry)
              }
            >
              <MoreVert fontSize='small' />
            </IconButton>
          </TableCell>
        </TableRow>
      );
    }

    const isMirror = entry.kind === 'mirror';
    const documentObject = entry.documentObject;

    return (
      <TableRow
        key={`entry-document-${documentObject.idDocument}-${index}`}
        hover
        sx={{ cursor: 'pointer' }}
        draggable={!isMirror && !props.isReadOnly}
        onDragStart={() =>
          !isMirror && props.onDragStartDocument(documentObject.idDocument)
        }
        onClick={() => handleOpenPreview(documentObject)}
        onContextMenu={(event: React.MouseEvent) =>
          handleOpenMenu(event, entry)
        }
      >
        <TableCell>
          <Stack direction='row' spacing={1} alignItems='center'>
            {getFileIcon(
              documentObject.FileName,
              documentObject.idMIMEType,
              mimeTypeArray
            )}
            <Typography variant='body2'>{documentObject.FileName}</Typography>
          </Stack>
        </TableCell>
        <TableCell>
          {getFileExtension(
            documentObject.FileName,
            documentObject.idMIMEType,
            mimeTypeArray
          ).toUpperCase() || 'Datei'}
        </TableCell>
        <TableCell>
          {isMirror
            ? (entry.documentObject as ISystemDirectoryDocument).origin
            : formatDate(documentObject.Created_at)}
        </TableCell>
        {props.downloadedArray !== undefined && (
          <TableCell>{getDownloadCount(documentObject.idDocument)}</TableCell>
        )}
        <TableCell
          align='right'
          onClick={(event: React.MouseEvent) => event.stopPropagation()}
        >
          <Tooltip title='Bild-in-Bild öffnen'>
            <IconButton
              size='small'
              onClick={() => handleOpenSidepanel(documentObject)}
            >
              <PictureInPicture fontSize='small' />
            </IconButton>
          </Tooltip>
          <Tooltip title='Vorschau'>
            <IconButton
              size='small'
              onClick={() => handleOpenPreview(documentObject)}
            >
              <FindInPage fontSize='small' />
            </IconButton>
          </Tooltip>
          <Tooltip title='Weitere Aktionen'>
            <IconButton
              size='small'
              onClick={(event: React.MouseEvent) =>
                handleOpenMenu(event, entry)
              }
            >
              <MoreVert fontSize='small' />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Paper
      variant='outlined'
      sx={{
        p: 2,
        height: '100%',
        position: 'relative',
        borderStyle: isDragActive ? 'dashed' : 'solid',
        borderColor: isDragActive ? 'primary.main' : undefined,
      }}
      {...getRootProps({
        onDragOver: (event: React.DragEvent) => {
          event.preventDefault();
          props.onDragOverDirectory(props.selectedDirectoryId);
        },
        onDrop: () => props.onDropOnDirectory(props.selectedDirectoryId),
      })}
    >
      {/* Drop-Overlay deckt den gesamten Ordnerinhalt ab — egal wo im Bereich
          die Datei losgelassen wird, der Upload wird ausgeloest. */}
      {isDragActive && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 10,
            bgcolor: 'action.hover',
            border: '2px dashed',
            borderColor: 'primary.main',
            borderRadius: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <CloudUpload sx={{ fontSize: 56, color: 'primary.main' }} />
          <Typography variant='h6' sx={{ mt: 1, color: 'primary.main' }}>
            Dateien hier ablegen
          </Typography>
          <Typography variant='body2' sx={{ mt: 0.5, color: 'text.secondary' }}>
            Die Dateien werden im Ordner „
            {currentDirectory?.Title ?? 'Interne Datenablage'}“ abgelegt.
          </Typography>
        </Box>
      )}
      <input
        {...getInputProps()}
        accept={mimeTypeArray.map(mimeType => mimeType.MIMEType).join(',')}
      />
      <input
        type='file'
        multiple
        hidden
        ref={uploadInputRef}
        accept={mimeTypeArray.map(mimeType => mimeType.MIMEType).join(',')}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          if (event.target.files !== null) {
            handleUpload(Array.from(event.target.files));
          }
          event.target.value = '';
        }}
      />

      <Dialog open={isDownloading} onClose={() => {}}>
        <DialogContent>
          <CircularProgress />
        </DialogContent>
      </Dialog>

      <DocumentPreviewDialog
        open={previewDocument !== null}
        localDocumentObject={previewDocument}
        mimeType={
          previewDocument === null
            ? undefined
            : getMimeTypeString(previewDocument.idMIMEType, mimeTypeArray)
        }
        onClose={() => setPreviewDocument(null)}
      />

      <Dialog
        open={renameEntry !== null}
        onClose={() => setRenameEntry(null)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>
          {renameEntry?.kind === 'directory'
            ? 'Ordner umbenennen'
            : 'Datei umbenennen'}
        </DialogTitle>
        <DialogContent>
          {renameEntry?.kind !== 'directory' && (
            <Alert severity='info' sx={{ mb: 2 }}>
              Eine Änderung der Dateiendung kann zu einer Beschädigung der Datei
              führen.
            </Alert>
          )}
          <TextField
            label={
              renameEntry?.kind === 'directory' ? 'Ordnername' : 'Dateiname'
            }
            value={renameValue}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setRenameValue(event.target.value)
            }
            size='small'
            fullWidth
            autoFocus
            inputProps={{ maxLength: 255 }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' onClick={() => setRenameEntry(null)}>
            Abbruch
          </Button>
          <Button
            variant='contained'
            disabled={renameValue.trim() === ''}
            onClick={handleConfirmRename}
          >
            Übernehmen
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDirectory !== null}
        onClose={() => setDeleteDirectory(null)}
        maxWidth='sm'
        fullWidth
        onClick={(event: React.MouseEvent) => event.stopPropagation()}
      >
        <DialogTitle>Ordner löschen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Der Ordner „{deleteDirectory?.Title}“ wird mit allen Unterordnern
            entfernt. Die Änderung wird mit dem Speichern des Vorgangs wirksam.
          </DialogContentText>

          {deleteDocumentCount > 0 && (
            <>
              <DialogContentText sx={{ mt: 2 }}>
                {deleteDocumentCount === 1
                  ? 'In dem Ordner liegt eine Datei.'
                  : `In dem Ordner liegen ${deleteDocumentCount} Dateien.`}{' '}
                Was soll damit passieren?
              </DialogContentText>
              <RadioGroup
                value={deleteDocumentMode}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setDeleteDocumentMode(event.target.value as 'delete' | 'move')
                }
                sx={{ mt: 1 }}
              >
                <FormControlLabel
                  value='move'
                  control={<Radio size='small' />}
                  label='Dateien in einen anderen Ordner verschieben'
                />
                <FormControlLabel
                  value='delete'
                  control={<Radio size='small' />}
                  label='Dateien mit löschen'
                />
              </RadioGroup>

              {deleteDocumentMode === 'move' && (
                <TextField
                  label='Zielordner'
                  value={deleteMoveTargetId}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setDeleteMoveTargetId(Number(event.target.value))
                  }
                  size='small'
                  select
                  fullWidth
                  sx={{ mt: 1 }}
                  helperText={
                    deleteMoveTargetArray.length === 0
                      ? 'Es steht kein anderer Ordner zur Verfügung — legen Sie zuerst einen an.'
                      : undefined
                  }
                >
                  {deleteMoveTargetArray.map(entry => (
                    <MenuItem
                      key={`delete-move-target-${entry.idDocumentDirectory}`}
                      value={entry.idDocumentDirectory}
                    >
                      {`${'  '.repeat(entry.depth)}${entry.Title}`}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' onClick={() => setDeleteDirectory(null)}>
            Abbruch
          </Button>
          <Button
            variant='contained'
            color='error'
            disabled={
              deleteDocumentCount > 0 &&
              deleteDocumentMode === 'move' &&
              deleteMoveTargetId === ''
            }
            onClick={() => {
              if (deleteDirectory !== null) {
                props.onDeleteDirectory(
                  deleteDirectory.idDocumentDirectory,
                  deleteDocumentCount > 0 &&
                    deleteDocumentMode === 'move' &&
                    deleteMoveTargetId !== ''
                    ? deleteMoveTargetId
                    : undefined
                );
              }
              setDeleteDirectory(null);
            }}
          >
            Löschen
          </Button>
        </DialogActions>
      </Dialog>

      <DocumentDownloadedDialog
        open={downloadedDocument !== null}
        onClose={() => setDownloadedDocument(null)}
        localDocumentObject={downloadedDocument as IDocument}
        assignedLegalClients={props.assignedLegalClients ?? []}
      />

      {/* Die Verschieben-/Umbenennen-Dialoge stehen ausserhalb des Menues —
          sonst wuerden sie mit ihm unmountet und sofort wieder schliessen. */}
      {transferActions.dialogs}

      <Menu
        open={menuPosition !== null}
        onClose={handleCloseMenu}
        anchorReference='anchorPosition'
        anchorPosition={menuPosition ?? undefined}
      >
        <MenuList dense>
          {menuEntry?.kind === 'directory' && (
            <MenuItem
              onClick={() => {
                props.setSelectedDirectoryId(
                  menuEntry.directory.idDocumentDirectory
                );
                handleCloseMenu();
              }}
            >
              <ListItemIcon>
                <CreateNewFolder fontSize='small' />
              </ListItemIcon>
              <ListItemText>Öffnen</ListItemText>
            </MenuItem>
          )}
          {menuEntry !== null && menuEntry.kind !== 'directory' && (
            <MenuItem
              onClick={() => {
                handleDownload(menuEntry.documentObject);
                handleCloseMenu();
              }}
            >
              <ListItemIcon>
                <Download fontSize='small' />
              </ListItemIcon>
              <ListItemText>Herunterladen</ListItemText>
            </MenuItem>
          )}
          {menuEntry !== null &&
            menuEntry.kind !== 'directory' &&
            menuEntry.documentObject.idDocument > 0 && (
              <MenuItem
                onClick={() => {
                  setDownloadedDocument({
                    ...menuEntry.documentObject,
                    Created_at: '',
                    idDocumentDirectory: null,
                    idLegalTransaction: null,
                    perEmail: false,
                    isFromTemplate: false,
                  } as IDocument);
                  handleCloseMenu();
                }}
              >
                <ListItemIcon>
                  <Search fontSize='small' />
                </ListItemIcon>
                <ListItemText>Anzahl Downloads</ListItemText>
              </MenuItem>
            )}
          {menuEntry !== null &&
            menuEntry.kind !== 'mirror' &&
            !props.isReadOnly && <Divider />}
          {menuEntry !== null &&
            menuEntry.kind === 'document' &&
            !props.isReadOnly &&
            transferActions.menuItems}
          {/* Dateien werden ueber DocumentTransferActions umbenannt — hier bleibt
              nur das Umbenennen von Ordnern. */}
          {menuEntry !== null &&
            menuEntry.kind === 'directory' &&
            !props.isReadOnly && (
              <MenuItem
                onClick={() => {
                  handleStartRename(menuEntry);
                  handleCloseMenu();
                }}
              >
                <ListItemIcon>
                  <Edit fontSize='small' />
                </ListItemIcon>
                <ListItemText>Umbenennen</ListItemText>
              </MenuItem>
            )}
          {menuEntry !== null &&
            menuEntry.kind !== 'mirror' &&
            !props.isReadOnly && (
              <MenuItem
                onClick={() => {
                  if (menuEntry.kind === 'directory') {
                    handleStartDeleteDirectory(menuEntry.directory);
                  } else {
                    handleDeleteDocument(menuEntry.documentObject.idDocument);
                  }
                  handleCloseMenu();
                }}
              >
                <ListItemIcon>
                  <Delete fontSize='small' />
                </ListItemIcon>
                <ListItemText>Löschen</ListItemText>
              </MenuItem>
            )}
        </MenuList>
      </Menu>

      {renderBreadcrumbs()}

      <Stack
        direction='row'
        spacing={1}
        alignItems='center'
        sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}
      >
        <Button
          size='small'
          variant='outlined'
          startIcon={<CreateNewFolder />}
          disabled={props.isReadOnly || isSystem}
          onClick={() => props.onRequestNewDirectory(props.selectedDirectoryId)}
        >
          Neuer Ordner
        </Button>
        <Button
          size='small'
          variant='outlined'
          startIcon={<UploadFile />}
          disabled={!canModify || isRoot}
          onClick={() => uploadInputRef.current?.click()}
        >
          Dateien hochladen
        </Button>

        {/* Aktionen fuer den gerade geoeffneten Ordner — sonst muesste man dafuer
            in den Elternordner wechseln. */}
        {!isRoot && currentDirectory !== undefined && !props.isReadOnly && (
          <>
            <Button
              size='small'
              variant='text'
              startIcon={<Edit />}
              onClick={() =>
                handleStartRename({
                  kind: 'directory',
                  directory: currentDirectory,
                })
              }
            >
              Ordner umbenennen
            </Button>
            <Button
              size='small'
              variant='text'
              color='error'
              startIcon={<Delete />}
              onClick={() => handleStartDeleteDirectory(currentDirectory)}
            >
              Ordner löschen
            </Button>
          </>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Typography variant='caption' color='text.secondary'>
          {entryArray.length} Einträge
        </Typography>
      </Stack>

      {/* Kein dauerhaftes Ablagefeld — der Hinweis „Dateien hier ablegen"
          erscheint erst als Overlay, sobald eine Datei über den Bereich gezogen
          wird (siehe oben). */}
      {canModify && !isRoot && (
        <Typography
          variant='caption'
          color='text.secondary'
          sx={{ display: 'block', mb: 1.5 }}
        >
          Dateien lassen sich per Drag &amp; Drop in diesen Bereich ziehen.
          Dateien und Ordner innerhalb der Ablage können ebenfalls per Drag
          &amp; Drop verschoben werden.
        </Typography>
      )}

      {isSystem && (
        <Alert severity='info' sx={{ mb: 2 }}>
          {getDirectoryTypeInfo(directoryType).description} Der Inhalt kann hier
          nicht bearbeitet werden.
        </Alert>
      )}

      {isRoot && (
        <Alert severity='info' sx={{ mb: 2 }}>
          Dateien werden immer innerhalb eines Ordners abgelegt. Wählen Sie
          links einen Ordner aus oder legen Sie einen neuen an.
        </Alert>
      )}

      {uploadWarning !== '' && (
        <Alert
          severity='warning'
          sx={{ mb: 2 }}
          onClose={() => setUploadWarning('')}
        >
          {uploadWarning}
        </Alert>
      )}

      <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell width={180}>Art</TableCell>
            <TableCell width={220}>
              {isSystem ? 'Herkunft' : 'Erstellt am'}
            </TableCell>
            {props.downloadedArray !== undefined && (
              <TableCell width={130}>Heruntergeladen</TableCell>
            )}
            <TableCell width={110} align='right'>
              Aktionen
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {entryArray.length === 0 ? (
            <TableRow>
              <TableCell colSpan={props.downloadedArray === undefined ? 4 : 5}>
                <Typography variant='body2' color='text.secondary'>
                  Dieser Ordner ist leer.
                  {canModify &&
                    !isRoot &&
                    ' Dateien können per Drag & Drop abgelegt werden.'}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            entryArray.map((entry, index) => renderRow(entry, index))
          )}
        </TableBody>
      </Table>
    </Paper>
  );
};
